import { useState } from 'react';
import { RootState } from 'store';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { OrderPageParams } from 'order/interfaces/OrderPageParams';

import { CurtainTabsEnum } from 'curtain/enums/CurtainTabsEnum';
import { setCurtainTab } from 'curtain/store/curtainActions';

import UtilService from 'shared/services/util.service';
import { ButtonPrimaryCSS } from 'shared/components/Button';
import { ConfirmationModal } from 'shared/components/ConfirmationModal';
import { P } from 'shared/components/Typography';
import { Spacer } from 'shared/components/Layout';
import { Wrapper } from 'shared/components/Wrapper';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import { useHasPermissions } from 'shared/hooks/useHasPermissions';
import { userPermissionsValues } from 'shared/enum/userPermissionsEnum';
import { anzac } from 'shared/config/Colors';

import { ACKTestButtonEnum } from 'tests/enums/ACKTestEnums';
import { testId } from 'tests/utils';

import { SpecialOrderApprovalStatusEnum } from '../enums/SpecialOrderApprovalStatusEnum';
import { SpecialOrderApproval } from '../interface/SpecialOrderApproval';
import {
  approveSpecialOrderApproval,
  disapproveSpecialOrderApproval,
} from '../store/ACKActions';

const ActionButton = styled.button<{ bgColor?: string }>`
  ${ButtonPrimaryCSS}
  padding: 8px 16px;
  font-size: 12px;
  min-width: 110px;
  justify-content: center;
`;

const ButtonRow = styled(Wrapper)`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionText = styled(P)`
  font-size: 12px;
`;

interface SOAItemActionsProps {
  specialOrderApproval: SpecialOrderApproval;
}

const SOAItemActions = ({ specialOrderApproval }: SOAItemActionsProps) => {
  const dispatch = useAppDispatch();
  const { orderId } = useParams<OrderPageParams>();

  const [approveModalOpened, setApproveModalOpened] = useState(false);
  const [disapproveModalOpened, setDisapproveModalOpened] = useState(false);

  const isCurrentUserViewer = useSelector(
    (state: RootState) => state.orderReducer.isCurrentUserViewer
  );

  const canApproveOnBehalfOfDealer = useHasPermissions(
    userPermissionsValues.CSR_VIEW_ORDERBOARD
  );

  const isUserDealerOrSalesRep = useHasPermissions([
    userPermissionsValues.DEALER_VIEW_ORDERBOARD,
    userPermissionsValues.SALES_REPRESENTATIVE_VIEW_ORDER_BOARD,
  ]);

  const isPending =
    specialOrderApproval.status === SpecialOrderApprovalStatusEnum.PENDING;

  if (!isPending || isCurrentUserViewer) return null;

  const scrollToSOA = () => {
    requestAnimationFrame(() => {
      document
        .getElementById(`soa-${specialOrderApproval.id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const onApproveSuccess = (close: () => void) => {
    toast.success('You have successfully approved the Special Order Request.');
    close();
    UtilService.onPopupClose();
    scrollToSOA();
  };

  const onDisapproveSuccess = (close: () => void) => {
    toast.success(
      `You have successfully disapproved the Special Order Request.${
        isUserDealerOrSalesRep ? ' Please collaborate your changes.' : ''
      }`
    );
    dispatch(setCurtainTab(CurtainTabsEnum.COLLABORATION_TAB));
    close();
    UtilService.onPopupClose();
    scrollToSOA();
  };

  const onApprove = (close: () => void) => {
    dispatch(
      approveSpecialOrderApproval(
        {
          orderId,
          specialOrderApprovalIds: [specialOrderApproval.id],
        },
        () => onApproveSuccess(close)
      )
    );
  };

  const onDisapprove = (close: () => void) => {
    dispatch(
      disapproveSpecialOrderApproval(
        {
          orderId,
          specialOrderApprovalIds: [specialOrderApproval.id],
        },
        () => onDisapproveSuccess(close)
      )
    );
  };

  return (
    <>
      <Spacer h="16px" />

      <ButtonRow>
        <ActionButton
          bgColor={anzac}
          onClick={() => setApproveModalOpened(true)}
          {...testId(ACKTestButtonEnum.APPROVE_SOR_BTN)}
        >
          Approve
        </ActionButton>
        <ActionText>
          I have reviewed the item(s) and approve. Please order the special
          materials needed to complete my order.
        </ActionText>
      </ButtonRow>

      <Spacer h="12px" />

      <ButtonRow>
        <ActionButton
          onClick={() => setDisapproveModalOpened(true)}
          {...testId(ACKTestButtonEnum.DISAPPROVE_SOR_BTN)}
        >
          Disapprove
        </ActionButton>
        <ActionText>
          I have reviewed the item(s) and do not approve this request. I have
          included the reason under the appropriate line item(s) in
          collaboration. Please cancel this request.
        </ActionText>
      </ButtonRow>

      <ConfirmationModal
        title={
          canApproveOnBehalfOfDealer
            ? 'Approve Special Order'
            : 'Special Order Request'
        }
        buttonText="Approve"
        message={
          canApproveOnBehalfOfDealer
            ? 'Are you sure you want to approve this Special Order on behalf of the dealer?'
            : 'Are you sure you want to approve this Special Order Request?'
        }
        confirmBgColor={anzac}
        confirm={onApprove}
        opened={approveModalOpened}
        cancel={() => setApproveModalOpened(false)}
      />

      <ConfirmationModal
        title={
          canApproveOnBehalfOfDealer
            ? 'Canceling request'
            : 'Special Order Request'
        }
        buttonText="Disapprove"
        message={
          canApproveOnBehalfOfDealer
            ? 'Are you sure you want to cancel this Special Order on behalf of the dealer?'
            : 'Are you sure you want to disapprove this Special Order Request?'
        }
        confirm={onDisapprove}
        opened={disapproveModalOpened}
        cancel={() => setDisapproveModalOpened(false)}
      />
    </>
  );
};

export default SOAItemActions;
